import EditIcon from '@mui/icons-material/Edit';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Column,
  DataTable,
  Filter,
  FilterDefinition,
  QueryParams,
} from '../../components/DataTable';
import { AuthContext } from '../../context/AuthContext';
import { ApiService } from '../../services/api';
import {
  CropEmbeddingSearchResponse,
  EmbeddingSearchParams,
  HttpSortingOrderEnum,
  ProductEmbeddingCropSortField,
} from '../../types';
import { ImageZoomModal } from './components/ImageZoomModal';

interface EmbeddingsQueryParams {
  chainId: number | null;
  storeId: number | null;
  chainName: string | null;
  storeName: string | null;
  upc: string | null;
  extendedUpcSearch: boolean;
  includeItemNumber: boolean;
  active: boolean | null;
  userEmailPattern: string | null;
  source: string | null;
}

export const EmbeddingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const [editingUpc, setEditingUpc] = useState<{
    id: string;
    currentUpc: string;
  } | null>(null);
  const [newUpcValue, setNewUpcValue] = useState('');
  const [editingType, setEditingType] = useState<{
    id: string;
    currentType: string;
  } | null>(null);
  const [newTypeValue, setNewTypeValue] = useState('');
  const [editingOrientation, setEditingOrientation] = useState<{
    id: string;
    currentOrientation: string;
  } | null>(null);
  const [newOrientationValue, setNewOrientationValue] = useState('');
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [updatingActiveStates, setUpdatingActiveStates] = useState<Set<string>>(new Set());
  const [updatingManualReviewStates, setUpdatingManualReviewStates] = useState<Set<string>>(
    new Set()
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // Parse query parameters into structured data
  const queryParams = useMemo<EmbeddingsQueryParams>(
    () => ({
      chainId: searchParams.get('chain_id') ? parseInt(searchParams.get('chain_id')!) : null,
      storeId: searchParams.get('store_id') ? parseInt(searchParams.get('store_id')!) : null,
      chainName: searchParams.get('chain_name'),
      storeName: searchParams.get('store_name'),
      upc: searchParams.get('upc'),
      extendedUpcSearch: searchParams.get('extended_search') !== 'false',
      includeItemNumber: searchParams.get('include_item_number') === 'true',
      active:
        searchParams.get('active') === 'true'
          ? true
          : searchParams.get('active') === 'false'
            ? false
            : null,
      userEmailPattern: searchParams.get('user_email_pattern'),
      source: searchParams.get('source'),
    }),
    [searchParams]
  );

  // Parse highlight parameters into a map of cropId -> style
  const rowStyles = useMemo(() => {
    const highlights = searchParams.getAll('highlight');
    const map = new Map<string, React.CSSProperties>();

    highlights.forEach((highlight) => {
      const [cropId, color] = highlight.split(',');
      if (cropId && color) {
        map.set(cropId, { backgroundColor: `#${color}` });
      }
    });

    return map;
  }, [searchParams]);

  // Convert query params to DataTable filters
  const dataTableFilters = useMemo<Filter[]>(() => {
    const filters: Filter[] = [];
    if (queryParams.chainId !== null) {
      filters.push({
        field: 'chainId',
        value: queryParams.chainId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.storeId !== null) {
      filters.push({
        field: 'storeId',
        value: queryParams.storeId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.upc) {
      filters.push({
        field: 'normalizedUpc',
        value: queryParams.upc,
        operator: 'contains',
      });
    }
    if (queryParams.active !== null) {
      filters.push({
        field: 'isActive',
        value: queryParams.active.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.userEmailPattern) {
      filters.push({
        field: 'createdBy',
        value: queryParams.userEmailPattern,
        operator: 'contains',
      });
    }
    if (queryParams.source) {
      filters.push({
        field: 'source',
        value: queryParams.source,
        operator: 'equals',
      });
    }
    if (queryParams.extendedUpcSearch === true) {
      filters.push({
        field: 'extendedSearch',
        value: queryParams.extendedUpcSearch.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.includeItemNumber === true) {
      filters.push({
        field: 'includeItemNumber',
        value: queryParams.includeItemNumber.toString(),
        operator: 'equals',
      });
    }
    return filters;
  }, [queryParams]);

  // Update URL when filters change
  const handleFiltersChange = useCallback(
    (filters: Filter[]) => {
      const newParams = new URLSearchParams(searchParams);

      // Clear filter-related params
      newParams.delete('chain_id');
      newParams.delete('store_id');
      newParams.delete('upc');
      newParams.delete('active');
      newParams.delete('user_email_pattern');
      newParams.delete('source');
      newParams.delete('extended_search');
      newParams.delete('include_item_number');

      // Add new filter params
      filters.forEach((filter) => {
        if (filter.field === 'chainId' && filter.value) {
          newParams.set('chain_id', filter.value);
        } else if (filter.field === 'storeId' && filter.value) {
          newParams.set('store_id', filter.value);
        } else if (filter.field === 'normalizedUpc' && filter.value) {
          newParams.set('upc', filter.value);
        } else if (filter.field === 'isActive' && filter.value) {
          newParams.set('active', filter.value);
        } else if (filter.field === 'createdBy' && filter.value) {
          newParams.set('user_email_pattern', filter.value);
        } else if (filter.field === 'source' && filter.value) {
          newParams.set('source', filter.value);
        } else if (filter.field === 'extendedSearch' && filter.value) {
          newParams.set('extended_search', filter.value);
        } else if (filter.field === 'includeItemNumber' && filter.value) {
          newParams.set('include_item_number', filter.value);
        }
      });

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Handle search changes to update UPC query param
  const handleSearchChange = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(searchParams);

      if (searchValue.trim()) {
        newParams.set('upc', searchValue.trim());
      } else {
        newParams.delete('upc');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Define async data fetcher for DataTable
  const fetchEmbeddings = useCallback(
    async (params: QueryParams) => {
      // Get chainId from filters
      const chainIdFilter = params.filters?.find((f) => f.field === 'chainId');
      const chainId = chainIdFilter?.value ? parseInt(chainIdFilter.value) : queryParams.chainId;

      if (!token || !chainId) {
        return { data: [], total: 0 };
      }

      try {
        // Build filters from DataTable
        const storeIdFilter = params.filters?.find((f) => f.field === 'storeId');
        const upcFilter = params.filters?.find((f) => f.field === 'normalizedUpc');
        const activeFilter = params.filters?.find((f) => f.field === 'isActive');
        const sourceFilter = params.filters?.find((f) => f.field === 'source');
        const createdByFilter = params.filters?.find((f) => f.field === 'createdBy');
        const extendedSearchFilter = params.filters?.find((f) => f.field === 'extendedSearch');
        const includeItemNumberFilter = params.filters?.find(
          (f) => f.field === 'includeItemNumber'
        );

        // Map column fields to valid API sort fields
        const sortFieldMap: Record<string, ProductEmbeddingCropSortField> = {
          productEmbeddingCropId: '_id',
          normalizedUpc: 'normalized_upc',
          createdAt: 'created_at',
          updatedAt: 'updated_at',
          createdBy: 'created_by',
          storeId: 'store_id',
          // Default mapping for any unmapped fields
          _id: '_id',
          created_at: 'created_at',
          updated_at: 'updated_at',
          normalized_upc: 'normalized_upc',
          created_by: 'created_by',
          store_id: 'store_id',
        };

        // Get the mapped sort field or default to '_id' if not sortable
        const sortField = params.orderBy ? sortFieldMap[params.orderBy] || '_id' : '_id';

        const apiParams: EmbeddingSearchParams = {
          chainId: chainId,
          ...(storeIdFilter?.value && {
            storeId: parseInt(storeIdFilter.value),
          }),
          ...(upcFilter?.value && { normalizedUpcs: upcFilter.value }),
          ...(activeFilter?.value && {
            isActive: activeFilter.value === 'true',
          }),
          ...(sourceFilter?.value && {
            source: sourceFilter.value as 'scan' | 'other',
          }),
          ...(createdByFilter?.value && {
            createdBy: createdByFilter.value,
          }),
          extendedSearch: extendedSearchFilter?.value === 'true',
          includeItemNumber: includeItemNumberFilter?.value === 'true',
          sortField: sortField as ProductEmbeddingCropSortField,
          sortOrder: (params.orderDirection || 'desc') as HttpSortingOrderEnum,
          page: params.page + 1, // API uses 1-based pagination
          limit: params.pageSize,
        };

        // Add search to UPC if provided
        if (params.search) {
          apiParams.normalizedUpcs = params.search;
        }

        const data = await ApiService.searchEmbeddings(token, apiParams);
        return { data, total: data.length }; // Note: API might need to return total count separately
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch embeddings');
        console.error('Error fetching embeddings:', err);
        return { data: [], total: 0 };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, queryParams.chainId, refreshKey]
  );

  // Define custom filters based on searchEmbeddings API parameters
  const filterDefinitions: FilterDefinition[] = useMemo(
    () => [
      {
        field: 'chainId',
        title: 'Chain ID',
        type: 'number',
        placeholder: 'Enter chain ID (required)',
      },
      {
        field: 'storeId',
        title: 'Store ID',
        type: 'number',
        placeholder: 'Enter store ID',
      },
      {
        field: 'normalizedUpc',
        title: 'UPC',
        type: 'text',
        placeholder: 'Search by UPC...',
      },
      {
        field: 'isActive',
        title: 'Status',
        type: 'select',
        options: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ],
        placeholder: 'All',
      },
      {
        field: 'source',
        title: 'Source',
        type: 'select',
        options: [
          { value: 'at', label: 'Action Tool' },
          { value: 'control', label: 'Control' },
          { value: 'scan', label: 'Scan' },
          { value: 'other', label: 'Other' },
        ],
        placeholder: 'All sources',
      },
      {
        field: 'createdBy',
        title: 'Created By',
        type: 'text',
        placeholder: 'Filter by email...',
      },
      {
        field: 'extendedSearch',
        title: 'Extended Search',
        type: 'select',
        options: [{ value: 'true', label: 'Yes' }],
        placeholder: 'No',
      },
      {
        field: 'includeItemNumber',
        title: 'Include Item Number',
        type: 'select',
        options: [{ value: 'true', label: 'Yes' }],
        placeholder: 'No',
        disabled: !queryParams.upc,
      },
    ],
    [queryParams.upc]
  );

  // Handle toggle active state
  const handleToggleActive = useCallback(
    async (embeddingId: string, currentState: boolean) => {
      if (!token) return;

      // Add to updating set to show progress
      setUpdatingActiveStates((prev) => new Set(prev).add(embeddingId));

      try {
        await ApiService.updateEmbeddingActiveState(token, embeddingId, !currentState);
        // Clear error if any
        setError(null);

        // Small delay before refresh to ensure the loading state is visible
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Trigger refresh by updating the key
        setRefreshKey((prev) => prev + 1);
      } catch (err) {
        console.error('Error updating embedding active state:', err);
        setError(err instanceof Error ? err.message : 'Failed to update active state');
      } finally {
        // Remove from updating set
        setUpdatingActiveStates((prev) => {
          const newSet = new Set(prev);
          newSet.delete(embeddingId);
          return newSet;
        });
      }
    },
    [token]
  );

  // Handle toggle manual review state
  const handleToggleManualReview = useCallback(
    async (embeddingId: string, currentState: boolean) => {
      if (!token) return;

      // Add to updating set to show progress
      setUpdatingManualReviewStates((prev) => new Set(prev).add(embeddingId));

      try {
        await ApiService.updateEmbeddingManualReview(token, embeddingId, !currentState);
        // Clear error if any
        setError(null);

        // Small delay before refresh to ensure the loading state is visible
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Trigger refresh by updating the key
        setRefreshKey((prev) => prev + 1);
      } catch (err) {
        console.error('Error updating embedding manual review state:', err);
        setError(err instanceof Error ? err.message : 'Failed to update manual review state');
      } finally {
        // Remove from updating set
        setUpdatingManualReviewStates((prev) => {
          const newSet = new Set(prev);
          newSet.delete(embeddingId);
          return newSet;
        });
      }
    },
    [token]
  );

  const columns: Column<CropEmbeddingSearchResponse>[] = useMemo(
    () => [
      {
        field: 'productEmbeddingCropId',
        title: 'Crop ID',
        sortable: true, // Maps to '_id'
        width: '220px',
        render: (value: string | undefined) => (
          <span className="text-xs font-mono" title={value || 'N/A'}>
            {value || 'N/A'}
          </span>
        ),
      },
      {
        field: 'vizUrl',
        title: 'Image',
        width: '100px',
        render: (value: string) => (
          <div className="flex justify-center">
            <img
              src={value}
              alt="Product"
              className="h-16 w-16 object-contain cursor-pointer hover:scale-125 transition-transform"
              onClick={(e) => {
                e.stopPropagation();
                setZoomedImage(value);
              }}
            />
          </div>
        ),
      },
      {
        field: 'productName',
        title: 'Product Name',
        sortable: false, // Not a valid sort field
        filterable: true,
        width: '200px',
        render: (value: string | undefined) => value || 'N/A',
      },
      {
        field: 'normalizedUpc',
        title: 'Normalized UPC',
        sortable: true, // Maps to 'normalized_upc'
        filterable: true,
        width: '200px',
        render: (value: string, row: CropEmbeddingSearchResponse) => (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{value}</span>
            {row.productEmbeddingCropId && (
              <button
                className="text-blue-600 hover:text-blue-800 text-xs underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingUpc({
                    id: row.productEmbeddingCropId!,
                    currentUpc: value,
                  });
                  setNewUpcValue(value);
                }}
              >
                <EditIcon fontSize="small" />
              </button>
            )}
          </div>
        ),
      },
      { field: 'barcode', title: 'Barcode', sortable: false, width: '160px' }, // Not a valid sort field
      {
        field: 'itemNumber',
        title: 'Item Number',
        sortable: false,
        width: '130px',
        render: (value: number | undefined) => (
          <span className="font-mono text-sm">{value || '-'}</span>
        ),
      },
      {
        field: 'packageVariant',
        title: 'Package Variant',
        sortable: false, // Not a valid sort field
        width: '120px',
        render: (value: string | undefined) => value || '-',
      },
      { field: 'chainId', title: 'Chain ID', sortable: false, width: '110px' }, // Not a valid sort field
      { field: 'storeId', title: 'Store ID', sortable: true, width: '110px' }, // Maps to 'store_id'
      { field: 'imageId', title: 'Image ID', sortable: false, width: '120px' }, // Not a valid sort field
      {
        field: 'manualReview',
        title: 'Manual Review',
        sortable: false, // Not a valid sort field
        width: '140px',
        render: (value: boolean, row: CropEmbeddingSearchResponse) => {
          const isUpdating = row.productEmbeddingCropId
            ? updatingManualReviewStates.has(row.productEmbeddingCropId)
            : false;

          return (
            <div className="flex items-center justify-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  disabled={isUpdating}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (row.productEmbeddingCropId && !isUpdating) {
                      handleToggleManualReview(row.productEmbeddingCropId, value);
                    }
                  }}
                  className="sr-only peer"
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className={`
                w-11 h-6 rounded-full peer transition-all duration-200 relative
                ${
                  isUpdating
                    ? 'bg-gray-300 cursor-not-allowed opacity-75'
                    : 'bg-gray-200 peer-checked:bg-green-600 cursor-pointer'
                }
                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:rounded-full after:h-5 after:w-5
                after:transition-all after:duration-200
                peer-checked:after:translate-x-full peer-checked:after:border-white
                ${isUpdating ? 'after:opacity-50' : ''}
              `}
                >
                  {isUpdating && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="absolute inset-0 bg-white bg-opacity-40 rounded-full"></div>
                      <svg
                        className="animate-spin h-3 w-3 text-blue-600 relative z-20"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </label>
            </div>
          );
        },
      },
      {
        field: 'source',
        title: 'Source',
        sortable: false, // Not a valid sort field
        width: '90px',
        render: (value: string) => (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              value === 'scan' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value}
          </span>
        ),
      },
      {
        field: 'isSynthetic',
        title: 'Synthetic',
        sortable: false, // Not a valid sort field
        width: '110px',
        render: (value: boolean) => (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              value ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        field: 'isPending',
        title: 'Pending',
        sortable: false, // Not a valid sort field
        width: '110px',
        render: (value: boolean) => (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              value ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {value ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        field: 'isActive',
        title: 'Status',
        sortable: false, // Not a valid sort field
        width: '120px',
        render: (value: boolean, row: CropEmbeddingSearchResponse) => {
          const isUpdating = row.productEmbeddingCropId
            ? updatingActiveStates.has(row.productEmbeddingCropId)
            : false;

          return (
            <div className="flex items-center justify-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  disabled={isUpdating}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (row.productEmbeddingCropId && !isUpdating) {
                      handleToggleActive(row.productEmbeddingCropId, value);
                    }
                  }}
                  className="sr-only peer"
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className={`
                w-11 h-6 rounded-full peer transition-all duration-200 relative
                ${
                  isUpdating
                    ? 'bg-gray-300 cursor-not-allowed opacity-75'
                    : 'bg-gray-200 peer-checked:bg-green-600 cursor-pointer'
                }
                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300
                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:rounded-full after:h-5 after:w-5 
                after:transition-all after:duration-200
                peer-checked:after:translate-x-full peer-checked:after:border-white
                ${isUpdating ? 'after:opacity-50' : ''}
              `}
                >
                  {isUpdating && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="absolute inset-0 bg-white bg-opacity-40 rounded-full"></div>
                      <svg
                        className="animate-spin h-3 w-3 text-blue-600 relative z-20"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </label>
            </div>
          );
        },
      },
      {
        field: 'orientation',
        title: 'Orientation',
        sortable: false, // Not a valid sort field
        width: '140px',
        render: (value: string, row: CropEmbeddingSearchResponse) => (
          <div className="flex items-center gap-2">
            <span className="text-sm">{value || '-'}</span>
            {row.productEmbeddingCropId && (
              <button
                className="text-blue-600 hover:text-blue-800 text-xs underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingOrientation({
                    id: row.productEmbeddingCropId!,
                    currentOrientation: value || '',
                  });
                  setNewOrientationValue(value || '');
                }}
              >
                <EditIcon fontSize="small" />
              </button>
            )}
          </div>
        ),
      },
      {
        field: 'classificationType',
        title: 'Type',
        sortable: false, // Not a valid sort field
        width: '130px',
        render: (value: string, row: CropEmbeddingSearchResponse) => {
          const typeColors = {
            product: 'bg-purple-100 text-purple-800',
            carton: 'bg-orange-100 text-orange-800',
            template_box: 'bg-indigo-100 text-indigo-800',
          };
          return (
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 rounded-full text-xs ${
                  typeColors[value as keyof typeof typeColors] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {value.replace('_', ' ')}
              </span>
              {row.productEmbeddingCropId && (
                <button
                  className="text-blue-600 hover:text-blue-800 text-xs underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingType({
                      id: row.productEmbeddingCropId!,
                      currentType: value || '',
                    });
                    setNewTypeValue(value || '');
                  }}
                >
                  <EditIcon fontSize="small" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        field: 'imageResolution',
        title: 'Resolution',
        sortable: false, // Not a valid sort field
        width: '110px',
        render: (value: string) => <span className="text-xs font-mono">{value.toUpperCase()}</span>,
      },
      {
        field: 'coords',
        title: 'Coordinates',
        sortable: false,
        width: '120px',
        render: (value: { bbox: [number, number, number, number] } | undefined) => (
          <span className="text-xs font-mono" title={value ? `[${value.bbox.join(', ')}]` : 'N/A'}>
            {value ? `[${value.bbox[2] - value.bbox[0]}x${value.bbox[3] - value.bbox[1]}]` : '-'}
          </span>
        ),
      },
      {
        field: 'transformationMatrix',
        title: 'Transform Matrix',
        sortable: false,
        hidden: true, // Hidden by default as it's technical data
        width: '150px',
        render: (value: string | undefined) => (
          <span className="text-xs font-mono" title={value || 'N/A'}>
            {value ? `...${value.slice(-10)}` : '-'}
          </span>
        ),
      },
      {
        field: 'productImage',
        title: 'Product Image',
        width: '100px',
        render: (value: string | undefined) =>
          value ? (
            <div className="flex justify-center">
              <img
                src={value}
                alt="Product Reference"
                className="h-16 w-16 object-contain cursor-pointer hover:scale-125 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomedImage(value);
                }}
              />
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          ),
      },
      { field: 'createdBy', title: 'Created By', sortable: true, width: '250px' }, // Maps to 'created_by'
      {
        field: 'updatedBy',
        title: 'Updated By',
        sortable: false, // Not a valid sort field
        width: '250px',
        render: (value: string | undefined) => value || '-',
      },
      {
        field: 'createdAt',
        title: 'Created',
        sortable: true, // Maps to 'created_at'
        width: '170px',
        render: (value: string | undefined) => (
          <span className="text-xs">{value ? new Date(value).toLocaleString() : '-'}</span>
        ),
      },
      {
        field: 'updatedAt',
        title: 'Updated',
        sortable: true, // Maps to 'updated_at'
        width: '170px',
        render: (value: string | undefined) => (
          <span className="text-xs">{value ? new Date(value).toLocaleString() : '-'}</span>
        ),
      },
    ],
    [handleToggleActive, handleToggleManualReview, updatingActiveStates, updatingManualReviewStates]
  );

  // Handle update UPC
  const handleUpdateUpc = useCallback(async () => {
    if (!token || !editingUpc) return;

    try {
      await ApiService.updateEmbeddingUpc(token, editingUpc.id, newUpcValue);
      setEditingUpc(null);
      setNewUpcValue('');
      // Clear error if any
      setError(null);
      // Trigger refresh
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Error updating embedding UPC:', err);
      setError(err instanceof Error ? err.message : 'Failed to update UPC');
    }
  }, [token, editingUpc, newUpcValue]);

  // Handle update classification type
  const handleUpdateType = useCallback(async () => {
    if (!token || !editingType) return;

    try {
      await ApiService.updateEmbeddingClassificationType(token, editingType.id, newTypeValue);
      setEditingType(null);
      setNewTypeValue('');
      // Clear error if any
      setError(null);
      // Trigger refresh
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Error updating embedding classification type:', err);
      setError(err instanceof Error ? err.message : 'Failed to update classification type');
    }
  }, [token, editingType, newTypeValue]);

  // Handle update orientation
  const handleUpdateOrientation = useCallback(async () => {
    if (!token || !editingOrientation) return;

    try {
      await ApiService.updateEmbeddingOrientation(
        token,
        editingOrientation.id,
        newOrientationValue
      );
      setEditingOrientation(null);
      setNewOrientationValue('');
      // Clear error if any
      setError(null);
      // Trigger refresh
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Error updating embedding orientation:', err);
      setError(err instanceof Error ? err.message : 'Failed to update orientation');
    }
  }, [token, editingOrientation, newOrientationValue]);

  // Build comprehensive title with all applied filters
  const dataTableTitle = useMemo(() => {
    const parts: string[] = ['Embeddings Data'];
    const filters: string[] = [];

    if (queryParams.chainId !== null) {
      filters.push(`Chain: ${queryParams.chainId}`);
    }
    if (queryParams.storeId !== null) {
      filters.push(`Store: ${queryParams.storeId}`);
    }
    if (queryParams.upc) {
      filters.push(`UPC: ${queryParams.upc}`);
    }
    if (queryParams.active !== null) {
      filters.push(`Status: ${queryParams.active ? 'Active' : 'Inactive'}`);
    }
    if (queryParams.source) {
      filters.push(`Source: ${queryParams.source}`);
    }
    if (queryParams.userEmailPattern) {
      filters.push(`Created by: ${queryParams.userEmailPattern}`);
    }
    if (queryParams.extendedUpcSearch) {
      filters.push('Extended Search: Yes');
    }

    if (filters.length > 0) {
      parts.push(`(${filters.join(', ')})`);
    }

    return parts.join(' ');
  }, [queryParams]);

  const handleFindCandidates = useCallback(() => {
    const params = new URLSearchParams();

    if (queryParams.storeName) {
      params.set('store_name', queryParams.storeName.toString());
    }

    if (queryParams.chainId) {
      params.set('chain_id', queryParams.chainId.toString());
    }
    if (queryParams.upc) {
      params.set('upc', queryParams.upc);
    }

    navigate(`/embeddings/candidates?${params.toString()}`);
  }, [navigate, queryParams]);

  const handleBulkEdit = useCallback(() => {
    const params = new URLSearchParams();

    if (queryParams.chainId) {
      params.set('chain_id', queryParams.chainId.toString());
    }
    if (queryParams.upc) {
      params.set('upc', queryParams.upc);
    }
    if (queryParams.storeId) {
      params.set('store_id', queryParams.storeId.toString());
    } else {
      const selectedStoreId = searchParams.get('selected_store_id');
      if (selectedStoreId) {
        params.set('store_id', selectedStoreId);
      }
    }
    if (queryParams.storeName) {
      params.set('store_name', queryParams.storeName);
    }

    navigate(`/embeddings/detail?${params.toString()}`);
  }, [navigate, queryParams, searchParams]);

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Error: {error}
          </div>
        )}

        {!queryParams.chainId && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            Please provide chain_id parameter in the URL to fetch embeddings.
          </div>
        )}

        <div className="flex-1 min-h-0">
          <DataTable<CropEmbeddingSearchResponse>
            title={dataTableTitle}
            titleAction={
              queryParams.upc ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleFindCandidates}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Find Candidates
                  </button>
                  {queryParams.chainId && (
                    <button
                      onClick={handleBulkEdit}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                    >
                      Bulk edit
                    </button>
                  )}
                </div>
              ) : undefined
            }
            data={fetchEmbeddings}
            columns={columns}
            pageSize={100}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            searchable={true}
            filterable={true}
            filterDefinitions={filterDefinitions}
            initialFilters={dataTableFilters}
            onFiltersChange={handleFiltersChange}
            onSearchChange={handleSearchChange}
            tableKey="embeddings-table"
            defaultHiddenColumns={[
              'transformationMatrix' as keyof CropEmbeddingSearchResponse,
              'updatedBy' as keyof CropEmbeddingSearchResponse,
              'coords' as keyof CropEmbeddingSearchResponse,
              'packageVariant' as keyof CropEmbeddingSearchResponse,
            ]}
            infinitePagination={true}
            idKey="productEmbeddingCropId"
            rowStyles={rowStyles}
          />
        </div>

        {/* UPC Edit Modal */}
        {editingUpc && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Edit UPC</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current UPC
                  </label>
                  <p className="text-sm font-mono bg-gray-100 p-2 rounded">
                    {editingUpc.currentUpc}
                  </p>
                </div>
                <div>
                  <label htmlFor="new-upc" className="block text-sm font-medium text-gray-700 mb-1">
                    New UPC
                  </label>
                  <input
                    id="new-upc"
                    type="text"
                    value={newUpcValue}
                    onChange={(e) => setNewUpcValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter new UPC"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setEditingUpc(null);
                      setNewUpcValue('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateUpc}
                    disabled={!newUpcValue || newUpcValue === editingUpc.currentUpc}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Classification Type Edit Modal */}
        {editingType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Edit Classification Type</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Type
                  </label>
                  <p className="text-sm bg-gray-100 p-2 rounded">
                    {editingType.currentType || 'Not set'}
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="new-type"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    New Type
                  </label>
                  <select
                    id="new-type"
                    value={newTypeValue}
                    onChange={(e) => setNewTypeValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select type...</option>
                    <option value="product">Product</option>
                    <option value="carton">Carton</option>
                    <option value="template_box">Template Box</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setEditingType(null);
                      setNewTypeValue('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateType}
                    disabled={!newTypeValue || newTypeValue === editingType.currentType}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orientation Edit Modal */}
        {editingOrientation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Edit Orientation</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Orientation
                  </label>
                  <p className="text-sm bg-gray-100 p-2 rounded">
                    {editingOrientation.currentOrientation || 'Not set'}
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="new-orientation"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    New Orientation
                  </label>
                  <select
                    id="new-orientation"
                    value={newOrientationValue}
                    onChange={(e) => setNewOrientationValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select orientation...</option>
                    <option value="front">Front</option>
                    <option value="not-front">Not-Front</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setEditingOrientation(null);
                      setNewOrientationValue('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateOrientation}
                    disabled={
                      !newOrientationValue ||
                      newOrientationValue === editingOrientation.currentOrientation
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ImageZoomModal
          isOpen={!!zoomedImage}
          imageSrc={zoomedImage || ''}
          onClose={() => setZoomedImage(null)}
        />
      </div>
    </div>
  );
};

export default EmbeddingsPage;
